/**
 * IndexedDB storage backend for scene and session persistence.
 *
 * Replaces the previous localStorage approach, lifting the ~5-10 MB quota
 * to configurable limits (50 MB per scene, 128 MB cumulative).
 */

const DB_NAME = "poseref-scenes";
const DB_VERSION = 2;
const SCENE_STORE = "scenes";
const SESSION_STORE = "session";

/** Maximum size in bytes of a single scene entry. */
export const MAX_SCENE_BYTES = 50 * 1024 * 1024; // 50 MB

/** Maximum cumulative size in bytes across all scene + session entries. */
export const MAX_TOTAL_BYTES = 128 * 1024 * 1024; // 128 MB

// ── DB lifecycle ────────────────────────────────────────────────────

let dbPromise: Promise<IDBDatabase> | null = null;

function openSceneDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      // Version 2 is the persistence rewrite — scrap any v1 data.
      // Cache and session are user-affordances, not source of truth, and the
      // new codec cannot read the old document shape.
      for (const name of Array.from(db.objectStoreNames))
        db.deleteObjectStore(name);
      db.createObjectStore(SCENE_STORE, { keyPath: "key" });
      db.createObjectStore(SESSION_STORE, { keyPath: "key" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      dbPromise = null;
      reject(request.error);
    };
  });
  return dbPromise;
}

// ── Scene record shape ──────────────────────────────────────────────

export interface SceneRecord {
  /** Storage key (e.g. "poseref_scene_MyFile") */
  key: string;
  /** The serialized `SceneDocument` JSON string. */
  json: string;
  /** Byte size of `json` (length × 2 for UTF-16 parity). */
  sizeBytes: number;
  /** Unix ms timestamp of when this entry was last saved. */
  savedAt: number;
}

export interface SessionRecord {
  key: "session";
  /** The serialized `SessionDocument` JSON string. */
  json: string;
  /** Byte size of `json`. */
  sizeBytes: number;
}

// ── Scene CRUD ──────────────────────────────────────────────────────

export async function putScene(record: SceneRecord): Promise<void> {
  const db = await openSceneDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SCENE_STORE, "readwrite");
    tx.objectStore(SCENE_STORE).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getScene(key: string): Promise<SceneRecord | null> {
  const db = await openSceneDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SCENE_STORE, "readonly");
    const req = tx.objectStore(SCENE_STORE).get(key);
    req.onsuccess = () => resolve((req.result as SceneRecord) ?? null);
    req.onerror = () => reject(req.error);
  });
}

export async function deleteSceneRecord(key: string): Promise<void> {
  const db = await openSceneDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SCENE_STORE, "readwrite");
    tx.objectStore(SCENE_STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getAllSceneRecords(): Promise<SceneRecord[]> {
  const db = await openSceneDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SCENE_STORE, "readonly");
    const req = tx.objectStore(SCENE_STORE).getAll();
    req.onsuccess = () => resolve(req.result as SceneRecord[]);
    req.onerror = () => reject(req.error);
  });
}

export async function clearAllScenes(): Promise<void> {
  const db = await openSceneDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SCENE_STORE, "readwrite");
    tx.objectStore(SCENE_STORE).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ── Session CRUD ────────────────────────────────────────────────────

export async function putSession(record: SessionRecord): Promise<void> {
  const db = await openSceneDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SESSION_STORE, "readwrite");
    tx.objectStore(SESSION_STORE).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getSession(): Promise<SessionRecord | null> {
  const db = await openSceneDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SESSION_STORE, "readonly");
    const req = tx.objectStore(SESSION_STORE).get("session");
    req.onsuccess = () => resolve((req.result as SessionRecord) ?? null);
    req.onerror = () => reject(req.error);
  });
}

export async function clearSession(): Promise<void> {
  const db = await openSceneDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SESSION_STORE, "readwrite");
    tx.objectStore(SESSION_STORE).delete("session");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ── Size helpers ────────────────────────────────────────────────────

/** Sum of sizeBytes across all scene records. */
export async function totalSceneBytes(): Promise<number> {
  const records = await getAllSceneRecords();
  return records.reduce((sum, r) => sum + r.sizeBytes, 0);
}

/** Sum of sizeBytes across scene + session records. */
export async function totalBytes(): Promise<number> {
  const db = await openSceneDB();

  const sceneTotal = await new Promise<number>((resolve, reject) => {
    const tx = db.transaction([SCENE_STORE, SESSION_STORE], "readonly");
    const sceneReq = tx.objectStore(SCENE_STORE).getAll();
    const sessionReq = tx.objectStore(SESSION_STORE).getAll();
    let total = 0;
    sceneReq.onsuccess = () => {
      for (const r of sceneReq.result as SceneRecord[]) total += r.sizeBytes;
    };
    sessionReq.onsuccess = () => {
      for (const r of sessionReq.result as SessionRecord[])
        total += r.sizeBytes;
    };
    tx.oncomplete = () => resolve(total);
    tx.onerror = () => reject(tx.error);
  });

  return sceneTotal;
}
